import { updateSession } from '../../api/propertiesApi';
// src/components/PropertyReport/index.jsx
import React, { useMemo, useState } from "react";
import { Button } from "primereact/button";
import CustomContentDialog from "./components/CustomContentDialog.js";
import PDFPreviewEditor from "./PDFPreviewEditor.jsx";
import { generatePDF } from "./utils/pdfUtils.js";
import { buildEditableReportPrices } from "./utils/priceReportLayout.js";
import { resolveReportContact } from "./utils/contactReportLayout.js";
import { generateExcel } from "./utils/excelUtils.js";
import { completeSession } from "./utils/uploadUtils.js";
import { useNavigate } from "react-router-dom";
import {
  computePriceRecommendation,
  formatRecommendationPrice,
} from "./utils/priceRecommendation.js";
import { notify } from "../../utils/notify";
/**
 * Default del nombre del archivo. Usa la dirección del wizard si existe;
 * si no, cae a "Tasación-{fecha}". El user puede editarlo o dejarlo.
 * No se exige obligatorio para generar — antes era required y bloqueaba.
 */
function buildDefaultReportName(wizardData) {
  const addr =
    wizardData?.selectedPlace?.formatted_address ||
    wizardData?.selected_place?.formatted_address ||
    wizardData?.address ||
    "";
  // Tomamos los primeros 40 chars del primer segmento (calle + altura).
  const short = addr.split(",")[0]?.trim().slice(0, 40);
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  if (short) {
    // Sanitizamos para usar como filename — sacamos chars problemáticos.
    const safe = short.replace(/[/\\?%*:|"<>]/g, "").replace(/\s+/g, "-");
    return `Tasación-${safe}-${today}`;
  }
  return `Tasación-${today}`;
}

const PriceEstimatePanel = ({
  recommendation,
  disabled,
  reportBlocked,
  underLimit,
  overLimit,
  activeCount,
  minProps,
  maxProps,
  onOpen,
}) => {
  const primary = recommendation?.primary;
  const alternatives = recommendation?.alternatives || [];
  const currency = recommendation?.currency || "USD";
  const stats = recommendation?.stats || {};
  const suitability = recommendation?.suitability;

  if (!primary) {
    return null;
  }

  return (
    <section className="acm-estimate-panel" aria-label="Estimación de precio de tasación">
      <div className="acm-estimate-panel__header">
        <span className="acm-estimate-panel__icon">
          <i className="pi pi-chart-line" />
        </span>
        <div>
          <p>Antes de generar</p>
          <h3>Tu propiedad vale aproximadamente</h3>
        </div>
        {suitability && (
          <span
            className={`acm-estimate-panel__status acm-estimate-panel__status--${suitability.level}`}
          >
            {suitability.label}
          </span>
        )}
      </div>

      <button
        type="button"
        className="acm-estimate-panel__main"
        onClick={() => onOpen(primary)}
        disabled={disabled}
        title="Ver las cuatro alternativas en el informe"
      >
        <span className="acm-estimate-panel__main-label">Estimado recomendado</span>
        <strong>{formatRecommendationPrice(primary.value, currency)}</strong>
        <span>Ver las cuatro alternativas en el informe</span>
      </button>

      <div className="acm-estimate-panel__alternatives" aria-label="Recomendaciones alternativas">
        {alternatives.slice(0, 3).map((option) => (
          <button
            key={option.key}
            type="button"
            className={`acm-estimate-option acm-estimate-option--${option.intent}`}
            onClick={() => onOpen(option)}
            disabled={disabled}
            title={option.hint}
          >
            <span>{option.label}</span>
            <strong>{formatRecommendationPrice(option.value, currency)}</strong>
            <small>{option.hint}</small>
          </button>
        ))}
      </div>

      <div className="acm-estimate-panel__details">
        <span>{stats.used || 0} comparables usados</span>
        {stats.avgSimilarity != null && <span>{stats.avgSimilarity}% similitud promedio</span>}
        {stats.avgDays != null && <span>{stats.avgDays} días promedio publicados</span>}
        {stats.removed > 0 && <span>{stats.removed} atípico fuera de muestra</span>}
      </div>

      {reportBlocked && (
        <div className="acm-estimate-panel__blocker">
          {underLimit && `Activá ${minProps - activeCount} propiedad más para poder generar.`}
          {overLimit && `Sacá ${activeCount - maxProps} propiedades para poder generar.`}
        </div>
      )}
    </section>
  );
};
const PropertyReport = ({
  properties,
  wizardData,
  underLimit = false,
  overLimit = false,
  minProps = 2,
  maxProps = 12,
  sortControl = null,
  selectionPending = false,
}) => {
  const navigate = useNavigate();

  const [showDialog, setShowDialog] = useState(false);
  // Default basado en la dirección del wizard: "Tasación-{address}-{fecha}".
  // El user puede editarlo o dejarlo. No es bloqueante para generar.
  const [reportName, setReportName] = useState(() =>
    buildDefaultReportName(wizardData),
  );
  const [includeCustomReport] = useState(false);
  const [customContent, setCustomContent] = useState("");
  const [editCustomContentDialog, setEditCustomContentDialog] = useState(false);

  const [loadingReport, setLoadingReport] = useState(false); // overlay para generación local

  const [priceEdits, setPriceEdits] = useState({});
  const [contactDefaults, setContactDefaults] = useState(null);
  const [contactEdits, setContactEdits] = useState({});
  const reportContact = useMemo(
    () => contactDefaults ? resolveReportContact({}, { ...contactDefaults, ...contactEdits }) : null,
    [contactDefaults, contactEdits],
  );
  const updateReportContact = (key, value) => {
    setContactEdits((current) => ({ ...current, [key]: value }));
  };
  const [commissionPercent, setCommissionPercent] = useState(wizardData?.comparable_snapshot?.valuation?.commissionPercent || "");
  // IDs de páginas adicionales que el user ocultó del PDF. El editor las
  // suba acá vía onHiddenPagesChange y de acá las pasamos a generatePDF.
  const [hiddenPageIds, setHiddenPageIds] = useState([]);
  // Páginas vacías creadas por el user desde el editor — { id, title, body }[].
  const [customPages, setCustomPages] = useState([]);

  // States del flujo viejo (askSecond, savingVisible, bgPending, bgDoneFlash,
  // secondFormat) fueron reemplazados por flowStep / uploadedFile abajo.

  // Estado del flujo del informe — controla el overlay que el editor muestra
  // durante toda la generación. Antes el upload corría en background sin
  // feedback y a veces no terminaba sin que el user se enterara. Ahora
  // bloqueamos el cierre hasta que termina realmente.
  //   idle     → todo cerrado, sin generar
  //   generating → armando el PDF/Excel + descarga local
  //   uploading  → subiendo al storage
  //   done       → terminó, muestra URL + share
  //   error      → algo falló, muestra el mensaje
  const [flowStep, setFlowStep] = useState("idle");
  const [flowError, setFlowError] = useState(null);
  // { url, format } cuando termina el upload — para mostrar share buttons.
  const [uploadedFile, setUploadedFile] = useState(null);

  const recommendation = useMemo(
    () => computePriceRecommendation(properties || [], wizardData || {}),
    [properties, wizardData],
  );
  const snapshotProperties = wizardData?.comparable_snapshot?.properties || [];
  const usingSnapshot = properties?.length === snapshotProperties.length && properties?.length > 0
    && properties.every(p => p.is_historical && snapshotProperties.some(saved => saved.id === p.id && saved.price === p.price));
  const savedValuation = usingSnapshot ? wizardData?.comparable_snapshot?.valuation : null;
  const currencyMode = savedValuation?.currencyMode || recommendation.currency;
  const priceOptions = useMemo(() => savedValuation?.reportPrices?.length
    ? recommendation.options.map(option => ({ ...option, value: Number(savedValuation.reportPrices.find(p => p.key === option.key)?.value ?? option.value) }))
    : recommendation.options, [recommendation.options, savedValuation]);
  const reportPrices = useMemo(
    () => buildEditableReportPrices(priceOptions, priceEdits, currencyMode),
    [priceOptions, priceEdits, currencyMode],
  );

  const updateReportPrice = (key, value) => {
    const sourceValue = priceOptions.find((option) => option.key === key)?.value ?? null;
    setPriceEdits((current) => ({
      ...current,
      [key]: { value, sourceValue, currency: currencyMode },
    }));
  };

  /** =========================
   * Helpers de generación
   * ======================== */

  // Generar y descargar localmente (NO sube)
  const generateLocal = async (format, { download }) => {
    if (format === "pdf") {
      await generatePDF({
        properties,
        reportName,
        includeCustomReport,
        customContent,
        setLoadingReport, // overlay sólo para generación local
        wizardData,
        reportPrices,
        reportContact,
        currencyMode,
        commissionPercent,
        hiddenPageIds,
        customPages,
        options: { download, upload: false },
      });
    } else {
      await generateExcel({
        properties,
        reportName,
        setLoadingReport,
        wizardData,
        options: { download, upload: false },
      });
    }
  };

  // generateAndUploadBackground + kickoffBackgroundSaves eliminados — el
  // upload ahora es bloqueante y se hace en generateAndUploadCapturingUrl
  // (más abajo), que devuelve la URL del storage para los botones share.

  /** =========================
   * Flujo principal — secuencial y bloqueante
   * Antes el flow era: descarga → pregunta otro formato → upload en background
   * sin feedback → cierra dialog → navega. El user no veía cuándo terminaba
   * la subida y "a veces no terminaba".
   *
   * Ahora: descarga → uploadea bloqueando → muestra share buttons → el user
   * decide cuándo cerrar. Mucho más explícito y confiable.
   * ======================== */
  const handleGenerateFlow = async (format) => {
    if (properties?.length < minProps) {
      notify.warning(
        `Necesitás al menos ${minProps} propiedades activas para generar el informe.`,
      );
      return;
    }
    if (properties?.length > maxProps) {
      notify.warning(
        `Hay más de ${maxProps} propiedades. Reducí la selección para generar el informe.`,
      );
      return;
    }

    setFlowStep("generating");
    setFlowError(null);
    setUploadedFile(null);

    try {
      // 1) Generar + descargar local (segundos)
      await generateLocal(format, { download: true });

      // 2) Subir al backend — capturamos URL del storage
      setFlowStep("uploading");
      const result = await generateAndUploadCapturingUrl(format);

      // 3) Marcar la sesión como completa (no es crítico si falla)
      try {
        await completeSession(wizardData.session_id);
      } catch (e) {
        console.warn("completeSession falló:", e);
      }

      // 4) Mostrar la URL + botones share
      setUploadedFile({
        url: result?.url || null,
        expiresAt: result?.expires_at || null,
        format,
      });
      setFlowStep("done");
    } catch (error) {
      console.error(`Error en generación ${format}:`, error);
      setFlowError(error?.message || "Error desconocido");
      setFlowStep("error");
    }
  };

  // Igual que generateAndUploadBackground pero devuelve la URL del storage
  // (para los botones de share).
  const generateAndUploadCapturingUrl = async (format) => {
    const sid = wizardData?.session_id || sessionStorage.getItem('current_session');
    if (sid) await updateSession(sid, { comparables: properties, valuation_snapshot: { reportPrices, currencyMode, commissionPercent } });
    const noop = () => {};
    if (format === "pdf") {
      return await generatePDF({
        properties,
        reportName,
        includeCustomReport,
        customContent,
        setLoadingReport: noop,
        wizardData,
        reportPrices,
        reportContact,
        currencyMode,
        commissionPercent,
        hiddenPageIds,
        customPages,
        options: { download: false, upload: true },
      });
    } else {
      return await generateExcel({
        properties,
        reportName,
        setLoadingReport: noop,
        wizardData,
        options: { download: false, upload: true },
      });
    }
  };

  // Cierra el editor y vuelve al home. Solo se ejecuta cuando el flow terminó.
  const handleCloseAfterDone = () => {
    setFlowStep("idle");
    setUploadedFile(null);
    setShowDialog(false);
    setTimeout(() => navigate("/", { replace: true }), 50);
  };

  const handleSaveDialog = (newPrompt, newContent) => {
    setCustomContent(newContent);
    setEditCustomContentDialog(false);
    handleGenerateFlow("pdf");
  };

  // Sin beforeunload: si cierra pestaña se corta (limitación del navegador para blobs grandes).
  // La navegación dentro de la SPA (navigate) NO cancela los fetch en curso.

  const openEditor = () => setShowDialog(true);

  const reportBlocked = underLimit || overLimit;

  return (
    <div className="property-report-container">
      <PriceEstimatePanel
        recommendation={recommendation}
        disabled={loadingReport || reportBlocked || selectionPending}
        reportBlocked={reportBlocked}
        underLimit={underLimit}
        overLimit={overLimit}
        activeCount={properties?.length || 0}
        minProps={minProps}
        maxProps={maxProps}
        onOpen={openEditor}
      />

      <div className="property-report-actions-row">
        {sortControl}
        <Button
          label={
            selectionPending ? 'Cargando anuncio…' : overLimit
              ? `Sacá ${properties.length - maxProps}`
              : "Generar informe"
          }
          icon={reportBlocked ? "pi pi-lock" : "pi pi-file"}
          onClick={openEditor}
          className={`btn-generate-report${reportBlocked ? " btn-generate-report--blocked" : ""}`}
          disabled={loadingReport || reportBlocked || selectionPending}
          tooltip={
            underLimit
              ? `Seleccioná al menos ${minProps} propiedades activas. Tenés ${properties.length}.`
              : overLimit
                ? `El informe acepta hasta ${maxProps} propiedades. Tenés ${properties.length}.`
                : undefined
          }
          tooltipOptions={{ position: "top" }}
        />
      </div>

      {/* Las cuatro alternativas se editan sobre la misma hoja que se imprime. */}
      <PDFPreviewEditor
        visible={showDialog}
        onHide={() => setShowDialog(false)}
        reportName={reportName}
        setReportName={setReportName}
        reportPrices={reportPrices}
        onReportPriceChange={updateReportPrice}
        reportContact={reportContact}
        onReportContactChange={updateReportContact}
        onContactDefaultsChange={setContactDefaults}
        commissionPercent={commissionPercent}
        setCommissionPercent={setCommissionPercent}
        currencyMode={currencyMode}
        loadingReport={loadingReport}
        onGeneratePdf={() => handleGenerateFlow("pdf")}
        onGenerateExcel={() => handleGenerateFlow("excel")}
        wizardData={wizardData}
        properties={properties}
        onHiddenPagesChange={setHiddenPageIds}
        onCustomPagesChange={setCustomPages}
        flowStep={flowStep}
        flowError={flowError}
        uploadedFile={uploadedFile}
        onFlowClose={handleCloseAfterDone}
        onFlowReset={() => {
          setFlowStep("idle");
          setFlowError(null);
        }}
      />

      {/* Los modales viejos de "askSecond", "savingVisible" + banners
          bgPending/bgDoneFlash fueron reemplazados por el FlowOverlay del
          editor — un overlay grande con los 4 estados (generating, uploading,
          done, error). Lo que se elimina queda en git history. */}

      <CustomContentDialog
        visible={editCustomContentDialog}
        onHide={() => setEditCustomContentDialog(false)}
        properties={properties}
        initialUserPrompt={customContent}
        initialCustomContent={customContent}
        onSave={handleSaveDialog}
      />
    </div>
  );
};

export default PropertyReport;
